/**
 * @fileoverview Project import template documents into grid/list row shapes.
 * @module import-editor/utils/templateProjection
 *
 * Responsibilities:
 * - `toListItem` — project a Dexie `ImportTemplate` doc into the
 *   `ImportTemplateListItem` shape consumed by template grids and run flows.
 * - `sortGrouped` — arrange templates so shell templates appear with their
 *   variants immediately beneath them, with orphan variants and standalone
 *   templates sorted alphabetically by name.
 *
 * Dependencies:
 * - `@firesquirrel/local-db` for the `ImportTemplateListItem` contract
 *
 * Architecture:
 * - Pure projection / sort helpers — no I/O, no side effects, no host wiring.
 * - Host-specific behavior (the `scope` fallback) is injected via the
 *   `defaultScope` option so tenant-web and system-web can share one
 *   implementation without re-introducing duplicated copies.
 *
 * Notes:
 * - `toListItem` previously existed verbatim in both tenant-web and
 *   system-web template services. The only divergence was the `scope`
 *   default: tenant-web defaulted to `'system'`, system-web used `doc.scope`
 *   as-is. The `defaultScope` option collapses the implementation into one
 *   shared function.
 * - G-043 fix: when `defaultScope` is omitted, the function now defaults to
 *   `'system'` rather than projecting `doc.scope` verbatim. This enforces the
 *   `ImportTemplateListItem.scope` (REQUIRED) contract — `scope: undefined`
 *   can no longer be produced for any input. System-web is the system admin
 *   portal; a system-DB `importTemplates` document missing `scope` is most
 *   likely legacy system data, so `'system'` is the safe default. Hosts that
 *   need a different fallback (e.g. tenant-web) pass `defaultScope`
 *   explicitly.
 */

import type { ImportTemplateListItem } from '@firesquirrel/local-db';

/**
 * @contract ToListItemOptions
 * Purpose: Options for `toListItem` that vary between host apps.
 * Fields:  defaultScope — fallback value for the projected `scope` (and the
 *          `source` fallback chain) when the source document omits `scope`.
 *          When omitted, the function defaults to `'system'` so the
 *          `ImportTemplateListItem.scope` (REQUIRED) contract is always
 *          satisfied (G-043).
 * Rules:   Only `'system'` and `'department'` are valid scope values.
 * Sync:    N/A — pure projection option.
 */
export interface ToListItemOptions {
  /**
   * Fallback scope applied when `doc.scope` is null/undefined. Also threads
   * into the `source` fallback chain (`doc.source ?? doc.scope ?? defaultScope`).
   * Tenant-web passes `'system'` explicitly to document intent; system-web
   * omits it and relies on the `'system'` default (system-web is the system
   * admin portal, so a doc missing `scope` is treated as legacy system data).
   */
  defaultScope?: 'system' | 'department';
}

/**
 * @reusable toListItem
 * Usage:        Called by host import template services to project Dexie docs
 *               into the grid list shape.
 * Rules:        Presentation-only projection. Does not mutate the input doc.
 *               Never returns `scope: undefined` — `defaultScope` defaults to
 *               `'system'` when the caller omits it (G-043).
 *
 * Project a Dexie `ImportTemplate` document into the `ImportTemplateListItem`
 * shape. The `defaultScope` option controls the fallback for the `scope` and
 * `source` fields when the document does not declare a scope. When
 * `defaultScope` is omitted it defaults to `'system'`, so the
 * `ImportTemplateListItem.scope` (REQUIRED) contract is always satisfied.
 */
export function toListItem(
  doc: any,
  options?: ToListItemOptions,
): ImportTemplateListItem {
  const defaultScope = options?.defaultScope ?? 'system';
  const scope = doc.scope ?? defaultScope;
  const source = doc.source ?? doc.scope ?? defaultScope;
  return {
    _id:               doc._id,
    name:              doc.name,
    description:       doc.description,
    scope,
    dataMode:          doc.dataMode,
    importBehavior:    doc.importBehavior,
    visibility:        doc.visibility,
    parentTemplateId:  doc.parentTemplateId ?? undefined,
    fieldMappingCount: Array.isArray(doc.fieldMappings)
      ? doc.fieldMappings.length
      : (doc.fieldMappingCount ?? 0),
    // Draft surface — project into the list so grid classifiers and
    // run-flow filters can make decisions without a second document fetch.
    // Note for tenant-web callers: in Phase-1 tenant-web only sees
    // system-published templates (buildSystemLibraryFilter requires
    // status='published'), so isDraft is always false there today. It is
    // projected anyway so the Phase-2 tenant-authored-draft story has no
    // schema gap to close.
    isDraft:           doc.isDraft === true || doc.status === 'draft',
    status:            doc.status,
    origin:            doc.origin,
    originTenantId:    doc.originTenantId,
    lastRunAt:         doc.lastRunAt,
    lastRunStatus:     doc.lastRunStatus,
    totalRuns:         doc.totalRuns,
    createdAt:         doc.createdAt,
    updatedAt:         doc.updatedAt,
    source,
  };
}

/**
 * @reusable sortGrouped
 * Usage:        Called by host import template services after `toListItem` to
 *               order templates for grid/list display.
 * Rules:        Pure sort — does not mutate the input array.
 *
 * Arrange templates so shells appear with their variants immediately beneath
 * them. Orphan variants (whose parent is not in the list) and standalone
 * templates fall in with the standalone group. All groups are sorted
 * alphabetically by name.
 */
export function sortGrouped(
  items: ImportTemplateListItem[],
): ImportTemplateListItem[] {
  const byId = new Map(items.map(t => [t._id, t]));
  const shells: ImportTemplateListItem[] = [];
  const standalone: ImportTemplateListItem[] = [];
  const variantsByParent = new Map<string, ImportTemplateListItem[]>();

  for (const t of items) {
    const isVariant = !!t.parentTemplateId && byId.has(t.parentTemplateId);
    if (isVariant) {
      const list = variantsByParent.get(t.parentTemplateId!) ?? [];
      list.push(t);
      variantsByParent.set(t.parentTemplateId!, list);
      continue;
    }
    if (!t.parentTemplateId && (t.fieldMappingCount ?? 0) === 0) {
      shells.push(t);
    } else {
      standalone.push(t);
    }
  }

  const nameCmp = (a: ImportTemplateListItem, b: ImportTemplateListItem) =>
    a.name.localeCompare(b.name);
  shells.sort(nameCmp);
  standalone.sort(nameCmp);

  const out: ImportTemplateListItem[] = [];
  for (const shell of shells) {
    out.push(shell);
    const variants = (variantsByParent.get(shell._id) ?? []).sort(nameCmp);
    out.push(...variants);
  }
  out.push(...standalone);
  return out;
}
